import { useQuery } from '@tanstack/react-query';
import { memo, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Dispatch } from 'redux';
import { Table } from 'rsuite';
import { RootState } from '../../../../../../../@types/RootState';
import { Sort } from '../../../../../../../@types/sort';
import { CustomHeaderCell, Layout } from '../../../../../../../components';
import { selectorRulesPageFiltersName } from '../../../../../../../reducers/ipa/strategy/rulesPage/filters/name';
import { selectorRulesPageFiltersType } from '../../../../../../../reducers/ipa/strategy/rulesPage/filters/type';
import { selectorRulesPageFiltersValue } from '../../../../../../../reducers/ipa/strategy/rulesPage/filters/value';
import {
  RESET_RULES_TABLE_DATAKEYS,
  RulesPageCustomRuleSortOptions,
  RulesPageDataKeyState,
  selectorRulesPageTableDatakeys,
  UPDATE_RULES_TABLE_DATAKEYS,
} from '../../../../../../../reducers/ipa/strategy/rulesPage/table/datakeys';
import {
  RulesPagePaginationState,
  selectorRulesPageTablePagination,
  UPDATE_RULES_TABLE_PAGINATION,
} from '../../../../../../../reducers/ipa/strategy/rulesPage/table/pagination';
import { selectorRulesPageTableSort, SET_RULES_TABLE_SORT } from '../../../../../../../reducers/ipa/strategy/rulesPage/table/sort';
import { SET_RULES_TOTAL_ELEMENTS } from '../../../../../../../reducers/ipa/strategy/rulesPage/table/totalElements';
import { listRulesQuery } from '../../lib/queries';
import { RuleDataItem } from '../../lib/service';
import { getRuleModel, ruleCustomRortOptionsMap, ruleCustomSortList, rulePaginationLengthMenu } from '../../lib/utils';
import RulePageTablePriceCell from '../RulePageTablePriceCell/RulePageTablePriceCell';
import RulePageTableRuleCell from '../RulePageTableRuleCell/RulePageTableRuleCell';
import RulePageTableStoreCell from '../RulePageTableStoreCell/RulePageTableStoreCell';
import RulesPageTableActionsCell from '../RulesPageTableActionsCell/RulesPageTableActionsCell';
import RulesPageTableFactorCell from '../RulesPageTableFactorCell/RulesPageTableFactorCell';
import RulesPageTableMechanismCell from '../RulesPageTableMechanismCell/RulesPageTableMechanismCell';
import RulesPageTableRecurrenceCell from '../RulesPageTableRecurrenceCell/RulesPageTableRecurrenceCell';
import RulesPageTableVadalityCell from '../RulesPageTableVadalityCell/RulesPageTableVadalityCell';
import styles from './RulesPageTable.module.scss';

const { Column, HeaderCell, Cell, Pagination } = Table;

type RulesPageTableProps = RouteComponentProps & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const RulesPageTable = ({
  value,
  ruleName,
  ruleType,
  sort,
  datakeys,
  pagination,
  setTotalElements,
  setSort,
  resetDataKeys,
  updateDataKeys,
  updatePagination,
}: RulesPageTableProps) => {
  const model = useMemo(() => {
    return getRuleModel({
      name: ruleName,
      type: ruleType,
      value,
      sort,
      pagination,
    });
  }, [pagination, ruleName, ruleType, sort, value]);

  const { data, isLoading } = useQuery({
    ...listRulesQuery(model),
    onSuccess: ({ totalElements }) => setTotalElements(totalElements),
  });

  const handleSortColumn = useCallback((type: string, orderBy: Sort['orderBy']) => setSort({ type, orderBy }), [setSort]);

  const handleUpdateDatakeys = useCallback(
    (dataKey: RulesPageCustomRuleSortOptions, value: string) => {
      const newColumn = ruleCustomRortOptionsMap.get(dataKey) || dataKey;
      resetDataKeys();
      updateDataKeys({ [newColumn]: value });
    },
    [resetDataKeys, updateDataKeys],
  );

  const handleUpdateSortColumn = useCallback(
    (dataKey: RulesPageCustomRuleSortOptions, value: string) => {
      const isSameColumn = sort.type === value;
      const newOrderBy = isSameColumn ? (sort.orderBy === 'asc' ? 'desc' : 'asc') : 'asc';

      handleSortColumn(value, newOrderBy);
      handleUpdateDatakeys(dataKey, value);
    },
    [handleSortColumn, handleUpdateDatakeys, sort.type, sort.orderBy],
  );

  const handleChangePage = useCallback(
    (page: number) => {
      updatePagination({ page });
    },
    [updatePagination],
  );

  const handleChangePageSize = useCallback(
    (size: number) => {
      updatePagination({ size });
    },
    [updatePagination],
  );

  return (
    <Layout.Section className={styles.wrapper}>
      <Table
        data={data?.content ?? []}
        loading={isLoading}
        rowHeight={68}
        autoHeight
        sortType={sort.orderBy}
        sortColumn={sort.type}
        onSortColumn={handleSortColumn}
      >
        {/* @ts-expect-error coluna não pode receber children */}
        <Column sortable flexGrow={2}>
          <HeaderCell>
            <CustomHeaderCell dataKey={datakeys.rule} data={ruleCustomSortList} onChangeSort={handleUpdateSortColumn}>
              Regra
            </CustomHeaderCell>
          </HeaderCell>
          <Cell dataKey={datakeys.rule}>{(rowData: RuleDataItem) => <RulePageTableRuleCell {...rowData} />}</Cell>
        </Column>

        {/* @ts-expect-error coluna não pode receber children */}
        <Column sortable width={140}>
          <HeaderCell>Lojas</HeaderCell>
          <Cell dataKey="storeId">{(rowData: RuleDataItem) => <RulePageTableStoreCell {...rowData} />}</Cell>
        </Column>

        {/* @ts-expect-error coluna não pode receber children */}
        <Column sortable width={96}>
          <HeaderCell>Preço</HeaderCell>
          <Cell dataKey="rulePriceType">{(rowData: RuleDataItem) => <RulePageTablePriceCell {...rowData} />}</Cell>
        </Column>

        {/* @ts-expect-error coluna não pode receber children */}
        <Column sortable width={184}>
          <HeaderCell>Mecanismo de regra</HeaderCell>
          <Cell dataKey="mechanism.mechanismType">{(rowData: RuleDataItem) => <RulesPageTableMechanismCell {...rowData} />}</Cell>
        </Column>

        {/* @ts-expect-error coluna não pode receber children */}
        <Column sortable width={220}>
          <HeaderCell>% Fator da regra</HeaderCell>
          <Cell dataKey="mechanism.factor">{(rowData: RuleDataItem) => <RulesPageTableFactorCell {...rowData} />}</Cell>
        </Column>

        {/* @ts-expect-error coluna não pode receber children */}
        <Column sortable width={144}>
          <HeaderCell>Recorrência</HeaderCell>
          <Cell dataKey="recurrence.recurrenceType">{(rowData: RuleDataItem) => <RulesPageTableRecurrenceCell {...rowData} />}</Cell>
        </Column>

        {/* @ts-expect-error coluna não pode receber children */}
        <Column sortable width={132}>
          <HeaderCell>Vigência</HeaderCell>
          <Cell dataKey="recurrence.expirationDate">{(rowData: RuleDataItem) => <RulesPageTableVadalityCell {...rowData} />}</Cell>
        </Column>

        {/* @ts-expect-error coluna não pode receber children */}
        <Column width={68}>
          <HeaderCell></HeaderCell>
          <Cell dataKey="actions">{(rowData: RuleDataItem) => <RulesPageTableActionsCell {...rowData} />}</Cell>
        </Column>
        {/* @ts-expect-error coluna não pode receber children */}
        <Column width={58}>
          <HeaderCell></HeaderCell>
          <Cell dataKey="_blank"></Cell>
        </Column>
      </Table>
      <Pagination
        lengthMenu={rulePaginationLengthMenu}
        total={data?.totalElements || 0}
        activePage={pagination.page}
        displayLength={pagination.size}
        onChangePage={handleChangePage}
        onChangeLength={handleChangePageSize}
      />
    </Layout.Section>
  );
};

const mapStateToProps = (state: RootState) => ({
  value: selectorRulesPageFiltersValue(state),
  ruleName: selectorRulesPageFiltersName(state),
  ruleType: selectorRulesPageFiltersType(state),
  sort: selectorRulesPageTableSort(state),
  datakeys: selectorRulesPageTableDatakeys(state),
  pagination: selectorRulesPageTablePagination(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setTotalElements: (payload: number) => dispatch(SET_RULES_TOTAL_ELEMENTS(payload)),
  setSort: (payload: Partial<Sort>) => dispatch(SET_RULES_TABLE_SORT(payload)),
  resetDataKeys: () => dispatch(RESET_RULES_TABLE_DATAKEYS()),
  updateDataKeys: (payload: Partial<RulesPageDataKeyState>) => dispatch(UPDATE_RULES_TABLE_DATAKEYS(payload)),
  updatePagination: (payload: Partial<RulesPagePaginationState>) => dispatch(UPDATE_RULES_TABLE_PAGINATION(payload)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(memo(RulesPageTable)));
